import * as React from 'react';
import { observer } from 'mobx-react';
import { Input, Form } from 'antd';
import { resolve } from 'inversify-react';

import { FormatService } from 'src/core/services/format.service';

const { TextArea } = Input;
const FormItem = Form.Item;

interface TextViewProps {
	mode: ControlMode;
	onGetErrorMessage?: () => Promise<ValidationError[]>;
	validate?: (value: any) => boolean;
	placeholder?: string;
	label?: string;
	value?: string | number;
	required?: boolean;
	format?: ControlFormat;
	onChanged?: (newValue: any) => void;
	addonBefore?: React.ReactNode;
	addonAfter?: React.ReactNode;
	addonClass?: string;
	prefix?: React.ReactNode;
	suffix?: React.ReactNode;
	description?: React.ReactNode;
	rows?: number;
	onClick?: (ev: React.MouseEvent<HTMLInputElement>) => void;
	style?: React.CSSProperties;
	type?: string;
	withoutFormWrapper?: boolean;
	targetType?: TargetType;
	min?: number;
	max?: number;
}

interface TextViewState {
	value: any;
	errors: ValidationError[];
}

@observer
export class TextView extends React.Component<TextViewProps, TextViewState> {
	@resolve
	private formatService: FormatService;

	private targetTypeDescriptors: { [prop: string]: TargetTypeDescriptor } = {
		'string': {
			validate: () => true,
			parse: (input) => input
		},
		'int': {
			validate: (input, options) => /^\s*(?!$)[-]?\d*$/.test(input) && ((options.min == null || options.min <= parseInt(input)) && (options.max == null || parseInt(input) <= options.max)),
			parse: (input) => parseInt(input) || 0
		},
		'int+': {
			validate: (input, options) => /^\s*\d+$/.test(input) && ((options.min == null || options.min <= parseInt(input)) && (options.max == null || parseInt(input) <= options.max)),
			parse: (input) => parseInt(input) || 0
		},
		'float': {
			validate: (input, options) => /^\s*(?!$)[-]?\d*[.]?\d*$/.test(input) && ((options.min == null || options.min <= parseFloat(input)) && (options.max == null || parseFloat(input) <= options.max)),
			parse: (input) => parseFloat(input) || 0
		},
		'float+': {
			validate: (input, options) => /^\s*(?!$)\d*[.]?\d*$/.test(input) && ((options.min == null || options.min <= parseFloat(input)) && (options.max == null || parseFloat(input) <= options.max)),
			parse: (input) => parseFloat(input) || 0
		},

	};

	constructor(props: Readonly<TextViewProps>) {
		super(props);
		this.state = {
			value: props.value,
			errors: []
		};
	}

	public componentWillReceiveProps(nextProps: Readonly<TextViewProps>, nextContext: any) {
		if (this.props.value != nextProps.value) {
			this.setState({
				value: nextProps.value
			});
		}
	}

	public render(): React.ReactNode {
		const {
			addonAfter,
			addonBefore,
			addonClass,
			description,
			label,
			mode,
			onChanged,
			onClick,
			placeholder,
			prefix,
			required,
			rows,
			style,
			suffix,
			type,
			value,
			withoutFormWrapper
		} = this.props;

		let input: React.ReactNode;
		if (rows || type === 'textarea') {
			input = (
				<TextArea
					rows={rows}
					onChange={this.onChanged}
					placeholder={placeholder}
					value={this.formattedValue()}
					disabled={mode == 'display'}
					style={style}
				/>
			);
		} else {
			input = (
				<Input
					prefix={prefix != null ? prefix : null}
					suffix={suffix != null ? suffix : null}
					addonBefore={addonBefore != null ? addonBefore : null}
					addonAfter={addonAfter != null ? addonAfter : null}
					onChange={this.onChanged}
					placeholder={placeholder}
					value={this.formattedValue()}
					onClick={onClick}
					disabled={mode == 'display'}
					style={style}
					type={type}
				/>
			);
		}

		if (withoutFormWrapper) {
			return input;
		} else {
			return (
				<FormItem
					label={label}
					required={required}
					validateStatus={this.state.errors.length == 0 ? 'success' : 'error'}
					help={description}
					className={addonClass}
				>
					{input}
				</FormItem>
			);;
		}
	}

	private formattedValue(): string {
		return this.formatService.toString(this.props.format, this.state.value);
	}

	private defaultValidate(value: string): boolean {
		const { targetType, min, max } = this.props;
		if (targetType == null || value === '' || value == null) {
			return true;
		}

		return this.targetTypeDescriptors[targetType].validate(value, { min, max });
	};

	private parseValue(value: string): any {
		const { targetType } = this.props;
		if (targetType == null || value === '' || value == null) {
			return value;
		}

		return this.targetTypeDescriptors[targetType].parse(value);
	}

	private onChanged = async (newVal: any): Promise<void> => {
		const { format, validate, onChanged, onGetErrorMessage } = this.props;

		newVal = newVal.target ? newVal.target.value : newVal;
		if (newVal != null && (format == 'percent' || format == 'percent-full')) {
			newVal = newVal / 100;
		}

		if ((validate && !validate(newVal)) || !this.defaultValidate(newVal)) {
			return;
		}

		const parsed = this.parseValue(newVal);
		onChanged(parsed);
		this.setState({ value: newVal });

		if (onGetErrorMessage != null) {
			const errors = await onGetErrorMessage();
			this.setState({
				errors: errors
			});
		} else {
			this.setState({
				errors: []
			});
		}
	}
}

type TargetType = 'string' | 'int' | 'int+' | 'float' | 'float+'; 
interface TargetTypeDescriptor {
	validate(input: string, options: any): boolean;
	parse(input: string): any;
}



// WEBPACK FOOTER //
// ./src/core/ui/text.view.tsx